<script setup>
import { createSeededRng } from "@/components/Utils/functions";
import dayjs from "dayjs";
import { getCurrentInstance, ref } from "vue";
import colors from "vuetify/lib/util/colors";
import { africanCountries } from "@/utils/countries";

const { proxy } = getCurrentInstance();

const cardItems = ref([
  {
    icon: "mdi-account-key",
    title: proxy.$t("registeredUsersMsg"),
    text: "50",
    bgColor: `linear-gradient(to bottom right, hsl(95 37% 80%), hsl(95 37% 34%))`,
  },
  {
    icon: "mdi-search-web",
    title: proxy.$t("totalSiteVisitsMsg"),
    text: "3259",
    bgColor: `linear-gradient(to bottom right, hsl(41 59% 80%), hsl(41 59% 43%))`,
  },
  {
    icon: "mdi-file-upload",
    title: proxy.$t("uploadedFilesMsg"),
    text: "965",
    bgColor: `linear-gradient(to bottom right, hsl(95 37% 80%), hsl(95 37% 34%))`,
  },
  {
    icon: "mdi-file-download",
    title: proxy.$t("downloadedFilesMsg"),
    text: "2724",
    bgColor: `linear-gradient(to bottom right, hsl(41 59% 80%), hsl(41 59% 43%))`,
  },
]);

function getSiteVisits() {
  const noOfDaysForSiteVisit = 60;
  const rng = createSeededRng(noOfDaysForSiteVisit, 10, 201);
  let now = dayjs();
  return [...Array(noOfDaysForSiteVisit)].map((_) => {
    now = now.subtract(1, "day");
    return {
      x: now.valueOf(),
      y: rng(),
    };
  });
}

function getNoOfDocsByCountry() {
  const rng = createSeededRng(100, 6, 67);
  return africanCountries
    .map((country) => ({
      x: country,
      y: rng(),
    }))
    .filter((item) => item.y > 30);
}

const numberOfSiteVisitsSeries = ref([
  {
    name: proxy.$t("numberOfSiteVisitsMsg"),
    data: getSiteVisits(),
  },
]);

const numberOfViewsPerDocumentTypeSeries = ref([
  {
    name: proxy.$t("numberOfDocumentViewsMsg"),
    data: [
      {
        x: proxy.$t("publicationMessage"),
        y: "94",
      },
      {
        x: proxy.$t("IPBioSafetyLawsMessage"),
        y: "53",
      },
      {
        x: proxy.$t("mediaMessage"),
        y: "44",
      },
      {
        x: proxy.$t("projectsMessage"),
        y: "87",
      },
      {
        x: proxy.$t("opportunitiesMessage"),
        y: "68",
      },
    ],
  },
]);

const documentTypesSeries = ref({
  [proxy.$t("publicationMessage")]: 343,
  [proxy.$t("IPBioSafetyLawsMessage")]: 232,
  [proxy.$t("mediaMessage")]: 275,
  [proxy.$t("projectsMessage")]: 182,
  [proxy.$t("opportunitiesMessage")]: 153,
});

const documentsByCountry = ref([
  {
    name: proxy.$t("documentyByCountryMsg"),
    data: getNoOfDocsByCountry(),
  },
]);

const fileTypes = ref({
  PDF: 94,
  DOCX: 53,
  CSV: 44,
  XLSX: 87,
  JPEG: 68,
  PNG: 53,
  MP4: 68,
  MP3: 36,
});

const titleOptions = {
  align: "center",
  margin: 30,
  offsetX: 0,
  offsetY: 0,
  floating: false,
  style: {
    fontSize: "18px",
    fontWeight: "normal",
    // color: proxy.$vuetify.theme.themes.light.secondaryText,
    color: "#AAAAAA",
  },
};

function getToolbarOptions({ filename }) {
  return {
    export: {
      csv: {
        filename: "test",
      },
      svg: {
        filename,
      },
      png: {
        filename,
      },
    },
  };
}

const chartContainerHeight = "350px";
</script>

<template>
  <v-container class="py-16">
    <v-row>
      <v-col v-for="item in cardItems" :key="item.title" cols="12" md="3">
        <v-card
          class="rounded-xl"
          :style="{
            background: item.bgColor,
          }"
        >
          <v-card-text class="d-flex flex-column" :style="{ gap: '24px' }">
            <div class="d-flex align-center">
              <v-avatar
                size="40"
                :style="{
                  boxShadow: `0px 3px 1px -2px rgba(0, 0, 0, 0.2), 
                  0px 2px 2px 0px rgba(0, 0, 0, 0.14), 
                  0px 1px 5px 0px rgba(0, 0, 0, 0.12)`,
                }"
              >
                <v-icon class="">
                  {{ item.icon }}
                </v-icon>
              </v-avatar>

              <div class="ml-6 text-h6">
                {{ item.title }}
              </div>
            </div>
            <div class="ml-3 text-h4 font-weight-bold">{{ item.text }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="pt-10">
      <v-col cols="12" md="8">
        <v-card
          class="rounded-lg"
          :style="{
            height: chartContainerHeight,
            background: `linear-gradient(to bottom right, ${colors.grey.lighten3}, ${colors.grey.lighten5})`,
          }"
        >
          <v-card-text :style="{ height: '100%' }">
            <apexchart
              height="100%"
              type="bar"
              :series="numberOfViewsPerDocumentTypeSeries"
              :options="{
                xaxis: {
                  type: 'category',
                },
                colors: [$vuetify.theme.themes.light.primary],
                plotOptions: {
                  bar: {
                    horizontal: true,
                  },
                },
                title: {
                  ...titleOptions,
                  text: $t('numberOfDocumentViewsMsg'),
                },
              }"
            />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card
          class="rounded-lg pie-container"
          :style="{
            height: chartContainerHeight,
            background: `linear-gradient(to bottom right, ${colors.grey.lighten3}, ${colors.grey.lighten5})`,
          }"
        >
          <v-card-text :style="{ height: '100%' }">
            <apexchart
              height="100%"
              type="pie"
              :series="Object.values(documentTypesSeries)"
              :options="{
                labels: Object.keys(documentTypesSeries),
                legend: {
                  position: 'bottom',
                },
                toolbar: {
                  show: true,
                },
                colors: [
                  'hsl(30 20% 30%)',
                  'hsl(35 30% 40%)',
                  'hsl(40 40% 50%)',
                  'hsl(45 50% 60%)',
                  'hsl(50 60% 70%)',
                ],
                title: {
                  ...titleOptions,
                  text: $t('numberOfDocumentsMsg'),
                },
              }"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row class="pt-10">
      <v-col cols="12" md="4">
        <v-card
          class="rounded-lg"
          :style="{
            height: chartContainerHeight,
            background: `linear-gradient(to bottom right, ${colors.grey.lighten3}, ${colors.grey.lighten5})`,
          }"
        >
          <v-card-text :style="{ height: '100%' }">
            <apexchart
              height="100%"
              type="pie"
              :series="Object.values(fileTypes)"
              :options="{
                labels: Object.keys(fileTypes),
                legend: {
                  position: 'bottom',
                },
                toolbar: {
                  show: true,
                },
                colors: [
                  'hsl(60 30% 30%)',
                  'hsl(70 35% 35%)',
                  'hsl(80 40% 40%)',
                  'hsl(90 45% 45%)',
                  'hsl(100 50% 50%)',
                  'hsl(110 55% 55%)',
                  'hsl(120 60% 60%)',
                  'hsl(130 65% 65%)',
                ],
                title: {
                  ...titleOptions,
                  text: $t('numberOfFileTypesMsg'),
                },
              }"
            />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="8">
        <v-card
          class="rounded-lg"
          :style="{
            height: chartContainerHeight,
            background: `linear-gradient(to bottom right, ${colors.grey.lighten3}, ${colors.grey.lighten5})`,
          }"
        >
          <v-card-text :style="{ height: '100%' }">
            <apexchart
              height="100%"
              type="bar"
              :series="documentsByCountry"
              :options="{
                xaxis: {
                  type: 'category',
                },
                colors: [$vuetify.theme.themes.light.secondary],
                dataLabels: {
                  enabled: false,
                },
                title: {
                  ...titleOptions,
                  text: $t('documentyByCountryMsg'),
                },
              }"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row class="pt-10">
      <v-col cols="12">
        <v-card
          class="rounded-xl"
          :style="{
            height: chartContainerHeight,
            background: `linear-gradient(to bottom right, ${colors.grey.lighten3}, ${colors.grey.lighten5})`,
          }"
        >
          <v-card-text :style="{ height: '100%' }">
            <apexchart
              height="100%"
              type="line"
              :options="{
                // stroke: {
                //   curve: 'smooth',
                // },
                xaxis: {
                  type: 'datetime',
                },
                colors: [$vuetify.theme.themes.light.primary],
                title: {
                  ...titleOptions,
                  text: $t('numberOfSiteVisitsMsg'),
                },
              }"
              :series="numberOfSiteVisitsSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.pie-container :deep(.apexcharts-legend) {
  justify-content: center;
}
</style>
